import { isMobileNavbar } from '@services/utils.service';

const TOUR_STEP_IDS = {
  adaptation: {
    intro: 'adaptation-intro',
    list: 'adaptation-list',
    details: 'adaptation-details',
    addEntry: 'adaptation-add-entry',
    addPlan: 'adaptation-add-plan',
    mapControls: 'adaptation-map-controls',
    submitPlan: 'adaptation-submit-plan',
    addStory: 'adaptation-add-story',
  },
  learn: {
    intro: 'learn-intro',
    navigation: 'learn-navigation',
    search: 'learn-search',
    language: 'learn-language',
    quiz: 'learn-quiz',
    backToExplore: 'learn-back-to-explore',
  },
  risks: {
    intro: 'risks-intro',
    sidePanel: 'risks-side-panel',
    chooseDistrict: 'risks-choose-district',
    tabs: 'risks-tabs',
    hazardCategories: 'risks-hazard-categories',
    glossary: 'risks-glossary',
    mapOverview: 'risks-map-overview',
    mapLegend: 'risks-map-legend',
    scenarios: 'risks-scenarios',
    backgroundMap: 'risks-background-map',
    togglePanel: 'risks-toggle-panel',
  },
};

function prepareAdaptationSteps(t, isSmallScreen = false) {
  const isShortViewport = typeof window !== 'undefined' ? window.innerHeight < 720 : false;

  return [
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.intro },
      title: t('Intro module'),
      target: 'body',
      content: (
        <div>
          <p>
            {t('Welcome to "Explore adaptation stories & plans". This part shows ways to adapt to climate change risks. We will show you how everything works.')}
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.list },
      title: t('Plans & stories list'),
      target: '.my-second-adaptation-step',
      content: (
        <div>
          <p>
            {t('Adaptation plans are projects that people are planning to adapt to certain climate change risks. Click on "Plans" to only see adaptation plans in the list and on the map (in blue). Stories are successfully executed adaptation actions. If "Stories" is highlighted, only adaptation stories are visible in the list and on the map (in yellow).')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.details },
      title: t('Open and share'),
      target: '.my-third-adaptation-step',
      content: (
        <div>
          <p>
            {t('Click on "see more" to see the details of a story or plan. Click on "Share" to copy the link or scan the qr code to open it on your phone.')}
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.addEntry },
      title: t('How to add a plan or story'),
      target: '.my-fourth-adaptation-step',
      content: (
        <div>
          <p>
            {t('To add your own plan or story, click on "Add plan" or "Add story".')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.addPlan },
      title: t('Add a new plan'),
      target: '.my-fifth-adaptation-step',
      content: (
        <div>
          <p>
            {t('To add an adaptation plan you have to fill out all the required fields indicated with (*). Type in the information and click on "upload" to add an image (not required).')}
          </p>
        </div>
      ),
      placement: isSmallScreen ? 'center' : 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.mapControls },
      title: t('Map controls'),
      target: '.maplibregl-ctrl-top-right ',
      content: (
        <div>
          <p>
            {t('To add the location of your plan (address) you have to click on the map. Use the zoom buttons to zoom in and out on the map or use touch gestures. Click on the "zoom to my location" button to automatically zoom in to where you are now. Click on the search and type in a location to zoom in to an address or village.')}
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.submitPlan },
      title: t('Submit a plan'),
      target: '.my-seventh-adaptation-step',
      content: (
        <div>
          <p>
            {t('When you are done, click on "submit" to save your plan and go back to the list. The plan will appear in the list after approval of the administrator. You can click on cancel to go back without finishing, but be warned, all your input will be lost!')}
          </p>
        </div>
      ),
      placement: isShortViewport ? 'center' : 'top',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.adaptation.addStory },
      title: t('Add story'),
      target: '.my-eighth-adaptation-step',
      content: (
        <div>
          <p>
            {t('Adding a story is similar to adding a plan. If the story is a result of a plan, you can choose a linked plan to start from. Some information will be filled out automatically and you can always choose to change this.')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { back: t('Back'), last: t('Done') },
    },
  ];
}

function prepareLearnSteps(t, { breakpoint }) {
  const isMobile = breakpoint?.maxWidth < 480;

  return [
    {
      data: { stepId: TOUR_STEP_IDS.learn.intro },
      title: t('Introduction'),
      target: 'body',
      content: (
        <div>
          <p>
            {t('Welcome to “Learn about climate change”. This part is divided into 4 learning modules. We will show you how everything works.')}
          </p>
          
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.learn.navigation },
      title: t('How to navigate '),
      target: isMobile ? 'body' : '.my-second-learn-step',
      content: (
        <div>
          <p>
            {t('Click on a module to open it and start learning. Each module consists of chapters you can open on click. When you finish a chapter or module a checkmark will indicate your accomplishment.')}
          </p>
          
        </div>
      ),
      placement: isMobile ? 'center' : 'bottom',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.learn.search },
      title: t('Search / glossary '),
      target: isMobileNavbar(breakpoint) ? '#navbar-mobile-menu__search-button' : '.glossary-search__button',
      content: (
        <div>
          <p>
            {t('Use the search to look up difficult words by typing them in. Click on the word you want to know more about to navigate to the page where it is explained.')}
          </p>
          
        </div>
      ),
      placement: isMobileNavbar(breakpoint) ? 'bottom' : 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    // {
    //   data: { stepId: TOUR_STEP_IDS.learn.language },
    //   title: t('Language'),
    //   target: '.my-fourth-learn-step',
    //   content: (
    //     <div>
    //       <p>
    //         {t('Here you can switch between English (EN) and Chichewa (CH).')}
    //       </p>
    //     </div>
    //   ),
    //   placement: 'right',
    //   disableBeacon: true,
    //   locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    // },
    {
      data: { stepId: TOUR_STEP_IDS.learn.quiz },
      title: t('How to open quiz'),
      target: '.my-fifth-learn-step',
      content: (
        <div>
          <p>
            {t('Click here to open the quiz and start answering questions to review your knowledge about climate change.')}
          </p>
          
        </div>
      ),
      placement: isMobile ? 'bottom' : 'left',
      disableBeacon: true,
      locale: { skip: t('Skip'), back: t('Back'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.learn.backToExplore },
      title: t('Back to the explore page'),
      target: '.my-sixth-learn-step',
      content: (
        <div>
          <p>
            {t('Click here to go back to the explore page and navigate to another part of the Climate Champions tool.')}
          </p>
          
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { back: t('Back'), last: t('Done') },
    },
  ];
}

function prepareRiskSteps(t, { isSmallScreen = false, breakpoint } = {}) {
  const viewportWidth = breakpoint?.minWidth ?? window.innerWidth;
  const showMobilePanelToggle = viewportWidth < 768;
  const isShortViewport = typeof window !== 'undefined' ? window.innerHeight < 720 : false;

  return [
    {
      data: { stepId: TOUR_STEP_IDS.risks.intro },
      title: t('Intro module'),
      target: 'body',
      content: (
        <div>
          <p>
            {t('Welcome to "Explore climate risk data". This part enables the exploration of climate risks in Malawi and more specifically in Kasungu. We will show you how everything works.')}
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.sidePanel },
      title: t('Side panel'),
      target: '.my-second-risk-step',
      content: (
        <div>
          <p>
            {t('On the left, information about climate risks is shown in the side panel.')}
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.chooseDistrict },
      title: t('Choose district'),
      target: '.my-third-risk-step',
      content: (
        <div>
          <p>
            {t('Pick in the dropdown for which district you want to see the information. You can also choose the district by clicking on the map, the selected district is highlighted in green.')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.tabs },
      title: t('Tabs in side panel'),
      target: '.my-fourth-risk-step',
      content: (
        <div>
          <p>
            {t('Use the tabs to navigate from the overview showing risks per district in Malawi, to the hazard types temperature, water and wind. These include specific information only for Kasungu.')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.hazardCategories },
      title: t('Hazard categories'),
      target: '.my-fifth-risk-step',
      content: (
        <div>
          <p>
            {t('Each risk type can have one or more specific hazard categories. Click on the category or select it from the dropdown to see the detailed information for Kasungu.')}
          </p>
        </div>
      ),
      placement: isShortViewport ? 'center' : 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.glossary },
      title: t('Glossary'),
      target: '.my-sixth-risk-step',
      content: (
        <div>
          <p>
            {t('Sometimes words are highlighted in the text. Click on them to see a short description of the word and click on "learn more" to navigate to the learn section where the word is explained.')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.mapOverview },
      title: t('What you see on the map'),
      target: '.my-seventh-risk-step',
      content: (
        <div>
          <p>
            {t('On the right, the map shows the risk level for a representative point in each district.')}
          </p>
        </div>
      ),
      placement: isSmallScreen ? 'center' : 'left',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.mapLegend },
      title: t('Map legend and time selector'),
      target: '.my-eighth-risk-step',
      content: (
        <div>
          <p>
            {t('The colour represents the level of risk from low to very high. You can also select the time period with the time selector: baseline (1995-2024), 2025-2054 or 2055-2084. Your choice will have an effect on the information in the side panel and on the map to illustrate how the risk changes over time.')}
          </p>
        </div>
      ),
      placement: isShortViewport ? 'center' : 'top',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.scenarios },
      title: t('Emission scenarios'),
      target: '.my-ninth-risk-step',
      content: (
        <div>
          <p>
            {t('The climate risk information also depends on the selected emission scenario. Click and select the level of emissions to see how the information changes.')}
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.backgroundMap },
      title: t('Background map button'),
      target: '.my-tenth-risk-step',
      content: (
        <div>
          <p>
            {t('You can select the background map by clicking on this button. You can choose between satellite (aerial pictures) or a road map.')}
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
    {
      data: { stepId: TOUR_STEP_IDS.risks.togglePanel },
      title: t('Open/close side panel'),
      target: showMobilePanelToggle ? '.my-eleventh-risk-step-mobile' : '.my-eleventh-risk-step-desktop',
      content: (
        <div>
          <p>
            {t('Close the side panel to see the map full screen.')}
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      locale: { skip: t('Skip'), next: t('next') },
    },
  ];
}

// z index has to be higher than the one of navbar if you want it to be visible there
// https://docs.react-joyride.com/styling
const styles = {
  options: {
    overlayColor: 'transparent',
    zIndex: 99999999,
  },
  tooltip: {
    borderRadius: 8,
  },
  buttonNext: {
    borderRadius: 8,
  },
  spotlight: {
    outline: '9999px solid rgba(0, 0, 0, 0.8)',
    backgroundColor: 'transparent',  
  },
}

export {
  prepareAdaptationSteps,
  prepareLearnSteps,
  prepareRiskSteps,
  styles,
  TOUR_STEP_IDS,
};
